import { skipToken, useQueries, useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import * as React from 'react';

import type * as api from '@gitbook/api-client';

import { apiQuery } from '../../state';
import {
    checkImportRunMatchesTarget,
    EMPTY_IMPORT_RUNS,
    IMPORT_RUN_POLL_INTERVAL,
    type ImportRunTargetFilter,
    isImportRunActive,
    isRecentImportRun,
    mergeImportRun,
    mergeImportRuns,
    type StartedImportRun,
    withPolledImportRuns,
} from './importRuns';
import {
    getLiveImportRunsSnapshot,
    getReportedImportRunsSnapshot,
    getStartedImportRunsSnapshot,
    retainImportRunChannel,
    setReportedImportRun,
    subscribeToLiveImportRuns,
    subscribeToStartedImportRuns,
} from './importRunsStore';

/**
 * The import runs targeting a space or one of its change requests.
 *
 * The runs come from the space and change request the screen already fetched, so asking about
 * imports costs no request of its own. Whoever did not start the import sees the same thing as
 * whoever did, and a reload keeps showing it.
 */
export function useImportRuns(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    filter: ImportRunTargetFilter;
}): api.ContentImportRun[] {
    const { space, changeRequest, filter } = props;

    const started = useSyncExternalStore(
        subscribeToStartedImportRuns,
        getStartedImportRunsSnapshot
    );
    const live = useSyncExternalStore(subscribeToLiveImportRuns, getLiveImportRunsSnapshot);
    const polled = useSyncExternalStore(subscribeToLiveImportRuns, getReportedImportRunsSnapshot);

    const reported = React.useMemo(() => {
        const spaceRuns = space.imports ?? EMPTY_IMPORT_RUNS;
        const changeRequestRuns = changeRequest?.imports ?? EMPTY_IMPORT_RUNS;
        // The table of contents asks per page, so most calls have nothing to collate at all.
        if (changeRequestRuns.length === 0 && polled.size === 0) {
            return spaceRuns;
        }
        const byId = new Map(spaceRuns.map((run) => [run.id, run]));
        changeRequestRuns.forEach((run) => byId.set(run.id, run));
        return withPolledImportRuns(byId, polled);
    }, [space.imports, changeRequest?.imports, polled]);

    // Read field by field: most callers build the filter inline, so comparing the object itself
    // would recompute on every render.
    const { space: targetSpace, changeRequest: targetChangeRequest, page: targetPage } = filter;

    const runs = React.useMemo(() => {
        const merged = mergeImportRuns({ reported, live, started }).filter((run) =>
            checkImportRunMatchesTarget(run, {
                space: targetSpace,
                changeRequest: targetChangeRequest,
                page: targetPage,
            })
        );
        return merged.length > 0 ? merged : EMPTY_IMPORT_RUNS;
    }, [reported, live, started, targetSpace, targetChangeRequest, targetPage]);

    useWatchActiveImportRuns(runs, space.organization, started);

    return runs;
}

/**
 * The import runs targeting any space of a site.
 *
 * Unlike a space or change request, a site does not carry its runs: the endpoint serving it also
 * serves visitors of a published site, who have no business seeing where the content came from.
 */
export function useSiteImportRuns(props: {
    organizationId: string;
    site: api.Site;
}): api.ContentImportRun[] {
    const { organizationId, site } = props;

    const { data } = useQuery(
        apiQuery('listImportRuns', {
            organizationId,
            site: site.id,
        })
    );

    const live = useSyncExternalStore(subscribeToLiveImportRuns, getLiveImportRunsSnapshot);
    const started = useSyncExternalStore(
        subscribeToStartedImportRuns,
        getStartedImportRunsSnapshot
    );
    const polled = useSyncExternalStore(subscribeToLiveImportRuns, getReportedImportRunsSnapshot);

    const runs = React.useMemo(() => {
        const listed = data?.items ?? EMPTY_IMPORT_RUNS;
        const startedHere = started.filter((run) => run.site === site.id);
        const merged = mergeImportRuns({
            reported: withPolledImportRuns(new Map(listed.map((run) => [run.id, run])), polled),
            // The live map is app-wide, so it is narrowed to the runs of this site — otherwise an
            // import running in an unrelated space would be folded into the site's list.
            live: pickImportRuns(live, [
                ...listed.map((run) => run.id),
                ...startedHere.map((run) => run.id),
            ]),
            started: startedHere,
        }).filter(isRecentImportRun);
        return merged.length > 0 ? merged : EMPTY_IMPORT_RUNS;
    }, [data?.items, live, started, polled, site.id]);

    useWatchActiveImportRuns(runs, organizationId, started);

    return runs;
}

/**
 * The URL a page was imported from, if it was.
 */
export function usePageImportSourceURL(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    pageId: string;
}): string | null {
    const { space, changeRequest, pageId } = props;

    const filter = React.useMemo(
        (): ImportRunTargetFilter => ({
            space: space.id,
            changeRequest: changeRequest?.id ?? null,
        }),
        [space.id, changeRequest?.id]
    );
    const runs = useImportRuns({ space, changeRequest, filter });

    return React.useMemo(() => {
        for (const run of runs) {
            const page = run.pages?.find((page) => page.id === pageId);
            if (page?.sourceURL) {
                return page.sourceURL;
            }
        }
        return null;
    }, [runs, pageId]);
}

/**
 * One import run, by id: read from the server, then followed live on its channel while it runs.
 * For a surface that knows a run without its space at hand, such as the agent's transcript.
 * `null` when the server does not return it, as for a user who cannot edit its space.
 */
export function useImportRun(props: {
    organizationId: string | null;
    runId: string | null;
}): api.ContentImportRun | null | undefined {
    const { organizationId, runId } = props;

    // This run's entry only, so another import's progress does not render the caller again.
    const live = useSyncExternalStore(subscribeToLiveImportRuns, () =>
        runId ? getLiveImportRunsSnapshot().get(runId) : undefined
    );
    const { data: reported } = useQuery(getImportRunQuery(organizationId, runId));

    const run = mergeImportRun(reported ?? undefined, live);
    const active = run ? isImportRunActive(run) : false;

    React.useEffect(() => {
        if (!active || !runId) {
            return;
        }
        return retainImportRunChannel(runId);
    }, [active, runId]);

    return run ?? (reported === null ? null : undefined);
}

/**
 * Whether any of these runs can still make progress. A run the server has not reported yet was
 * only just started; one it does not return is left out, since its status cannot be known.
 */
export function useAnyImportRunActive(props: {
    organizationId: string | null;
    runIds: string[];
}): boolean {
    const { organizationId, runIds } = props;
    const reported = useQueries({
        queries: runIds.map((runId) => getImportRunQuery(organizationId, runId)),
    });

    // A boolean, so the caller renders again only when it flips, not on every progress update.
    return useSyncExternalStore(subscribeToLiveImportRuns, () =>
        runIds.some((runId, index) => {
            const data = reported[index]?.data;
            const run = mergeImportRun(data ?? undefined, getLiveImportRunsSnapshot().get(runId));
            return run ? isImportRunActive(run) : data !== null;
        })
    );
}

/**
 * The query reading a run from the server, polled only while the run can still make progress.
 */
function getImportRunQuery(organizationId: string | null, runId: string | null) {
    return {
        ...apiQuery(
            'getImportRun',
            organizationId && runId ? { organizationId, importRunId: runId } : skipToken,
            { nullOnError: true }
        ),
        refetchInterval: (query: { state: { data?: api.ContentImportRun | null } }) =>
            query.state.data === null || (query.state.data && !isImportRunActive(query.state.data))
                ? false
                : IMPORT_RUN_POLL_INTERVAL,
    };
}

/**
 * The entries of the app-wide live map belonging to a given set of runs.
 */
function pickImportRuns(
    live: ReadonlyMap<string, api.ContentImportRun>,
    ids: string[]
): ReadonlyMap<string, api.ContentImportRun> {
    const picked = new Map<string, api.ContentImportRun>();
    for (const id of ids) {
        const run = live.get(id);
        if (run) {
            picked.set(id, run);
        }
    }
    return picked;
}

/**
 * Keep the channels of the runs still going open, and only those — and ask the server about a run
 * whose channel may have nothing left to say.
 *
 * A run that ended has nothing left to stream, and its outcome is already on the resource. But a
 * workflow that dies stops publishing while its channel keeps replaying the last thing it said, so
 * a run displayed as going would stay that way for as long as the screen is open. The server
 * reports such a run as failed once it outlives its deadline — this is what asks. Runs started in
 * this tab are left to the toasts' own watchdog, which follows them across screens.
 */
function useWatchActiveImportRuns(
    runs: api.ContentImportRun[],
    organizationId: string,
    started: StartedImportRun[]
): void {
    const activeIds = runs
        .filter(isImportRunActive)
        .map((run) => run.id)
        .join(',');

    React.useEffect(() => {
        if (!activeIds) {
            return;
        }

        const releases = activeIds.split(',').map(retainImportRunChannel);
        return () => {
            releases.forEach((release) => release());
        };
    }, [activeIds]);

    // One run at a time: several stuck at once on one surface is not worth a query each. The runs
    // come newest first, so the last of them is the one whose deadline comes first.
    const watchedId = activeIds
        .split(',')
        .findLast((id) => id && !started.some((trace) => trace.id === id));

    const { data } = useQuery({
        ...apiQuery(
            'getImportRun',
            watchedId ? { organizationId, importRunId: watchedId } : skipToken
        ),
        refetchInterval: IMPORT_RUN_POLL_INTERVAL,
    });

    React.useEffect(() => {
        if (data) {
            setReportedImportRun(data);
        }
    }, [data]);
}
